import Payments from '~/stores/payments';
import api from '~/utils/api';
import {
  applyCreditMethodToClaimLineItems,
  pendingClaimLineItemToCalculatedLineItem,
} from '~/utils/claimLineItem';
import { ClaimType } from '~/utils/constants';
import { toCurrency } from '~/utils/formatters';
import {
  makeCrewClaimWithClaimLineItemsLoader,
  redirect,
} from '~/utils/routing';
import { CreditResolutionMethod } from '~/utils/schemas';
import { getCreditMethods } from './return-summary-credit-method';
import { getExchangeMethods } from './return-summary-exchange-method';
import { getReturnSummaryParams } from './return-summary-search-params';

// ----------- Refund Methods -----------

const getOutstandingBalance = (
  total: number,
  refundMethod: CreditResolutionMethod | null,
) => {
  // If the balance is positive, this indicates in favor of the merchant
  if (total > 0) {
    return {
      kind: 'Payment' as const,
      total,
      label: 'Payment due',
    };
  }

  if (total === 0 || !refundMethod) return null;

  return {
    total,
    kind: refundMethod,
    label: refundMethod === 'Refund' ? 'Refund owed' : 'Gift Card owed',
  };
};

export const { loader, useLoaderData: useReturnSummaryLoaderData } =
  makeCrewClaimWithClaimLineItemsLoader(
    async ({ params: { store }, context, request }) => {
      if (!context.crewClaim.returnMethod) {
        return redirect(`/${store}`);
      }

      const { settings, order, crewClaim } = context;
      const { currencyCode } = order;

      const params = getReturnSummaryParams(request.url);
      const creditMethod = getCreditMethods(
        context.settings,
        context.crewClaim.claimLineItems,
        params.creditMethod,
      );
      const exchangeMethod = getExchangeMethods(context, params.exchangeMethod);

      // ! It is expected at the API layer, than the claim line items will have the
      // ! creditMethod applied... should we apply it to the draft claim line item or keep intercepting as we are today
      const fromCustomerItems = applyCreditMethodToClaimLineItems(
        crewClaim.claimLineItems,
        creditMethod.selected,
      );

      const returnSummary = await api.calculateBeforeClaimSubmission({
        params: { storefrontId: settings.storefrontId },
        body: {
          id: order.id,
          claimType: ClaimType.return,
          calculatedLineItems: fromCustomerItems.map((claim) =>
            pendingClaimLineItemToCalculatedLineItem(claim),
          ),
          exchangeMethod: exchangeMethod.selected,
          returnShipment: crewClaim.returnMethod,
          toCustomerRate: crewClaim.toCustomerRate,
          creditResolutionMethod: creditMethod.selected ?? undefined,
        },
      });

      const { balance, paymentIntent } = returnSummary;

      if (paymentIntent) {
        Payments.setPaymentIntent(
          paymentIntent.clientSecret,
          paymentIntent.idFromPlatform,
        );
      }

      // TODO: it may make sense to include the payment intent in the balance object.. instead of separately
      // the max amount that can be can be charge to the customer
      const authorizedAmount = paymentIntent ? paymentIntent.amount : balance;

      const outstandingBalance = getOutstandingBalance(
        authorizedAmount,
        creditMethod.selected,
      );

      return {
        credits: returnSummary.credits,
        deductions: returnSummary.deductions,
        exchangeMethod,
        balance,
        creditMethod,
        authorizedAmount,
        paymentIntentSecret: paymentIntent?.clientSecret,
        outstandingBalance,
        currencyFormatter: toCurrency(currencyCode),
      };
    },
  );
